import { useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Loader2, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/app/providers/AuthProvider';
import { Button } from '@/components/ui/button';

export function LgpdConsentModal() {
  const { latestConsent, acceptLgpd, signOut } = useAuth();
  const [accepting, setAccepting] = useState(false);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  if (!latestConsent) return null;

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 32;
    if (atBottom) setScrolledToBottom(true);
  };

  const handleAccept = async () => {
    setAccepting(true);
    await acceptLgpd();
    setAccepting(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="lgpd-title"
    >
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex flex-shrink-0 items-center gap-3 border-b border-border px-6 py-5">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">
            <ShieldCheck className="text-primary" size={20} />
          </div>
          <div>
            <h2 id="lgpd-title" className="font-brand text-lg font-bold text-foreground">
              Termo de Consentimento LGPD
            </h2>
            <p className="text-xs text-muted-foreground">
              Versão {latestConsent.version} — Role até o final para aceitar
            </p>
          </div>
        </div>

        {/* Conteúdo com scroll nativo */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="min-h-0 flex-1 overflow-y-auto px-6 py-5"
        >
          <div className="prose prose-sm max-w-none prose-headings:font-brand prose-headings:text-foreground prose-p:text-foreground/80 prose-strong:text-foreground">
            <ReactMarkdown>{latestConsent.content_md}</ReactMarkdown>
          </div>

          {!scrolledToBottom && (
            <p className="mt-6 animate-bounce text-center text-xs text-muted-foreground">
              ↓ Role até o final para habilitar o botão de aceite
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-shrink-0 flex-col items-center justify-between gap-3 rounded-b-2xl border-t border-border bg-secondary/40 px-6 py-4 sm:flex-row">
          <p className="text-center text-xs text-muted-foreground sm:text-left">
            Ao aceitar, você concorda com o tratamento dos seus dados conforme a LGPD.
          </p>
          <div className="flex flex-shrink-0 gap-3">
            <Button variant="ghost" size="sm" onClick={signOut} className="text-muted-foreground">
              Recusar e sair
            </Button>
            <Button
              size="sm"
              className="min-w-[120px] bg-accent font-semibold text-accent-foreground hover:bg-accent/90"
              onClick={handleAccept}
              disabled={!scrolledToBottom || accepting}
            >
              {accepting ? (
                <>
                  <Loader2 size={14} className="mr-2 animate-spin" />
                  Registrando…
                </>
              ) : (
                'Li e aceito'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
